<!-- Универсальная таблица с сортировкой, строками и действиями. -->
<template>
  <div class="table-container" v-if="isTableReady">
    <div v-if="loading" class="table-overlay">
      <div class="table-overlay__spinner">
        <base-loader />
      </div>
    </div>

    <div
      ref="tableWrapper"
      class="table-wrapper"
      @click.self="closeContextMenu"
      @scroll="syncScrollFromBottom"
    >
      <table ref="table" class="table" @click.self="closeContextMenu">
        <thead>
          <tr>
            <th v-if="selectable" class="select-column" @click.stop>
              <BaseCheckbox
                v-if="selectable && multiple"
                :model-value="allSelected"
                @update:model-value="toggleAllRows"
              />
            </th>
            <th
              v-for="col in columns"
              :key="col.key"
              class="table-header"
              :class="{
                sortable: col.sortable,
              }"
              :style="{ 'min-width': `${col.minWidth}px` }"
              @click="col.sortable ? handleSort(col) : null"
            >
              <div class="header-content">
                <span class="header-text">
                  {{ col.label || col.key }}
                </span>
                <div v-if="col.sortable" class="sort-indicator">
                  <svg
                    class="sort-arrow down"
                    :class="{ active: isSortActive(col, 'desc') }"
                    width="10"
                    height="16"
                    viewBox="0 0 10 16"
                  >
                    <path
                      d="M5 14L1 10H9L5 14Z"
                      fill="currentColor"
                    />
                    <rect
                      x="4"
                      y="2"
                      width="2"
                      height="8"
                      fill="currentColor"
                    />
                  </svg>
                  <svg
                    class="sort-arrow up"
                    :class="{ active: isSortActive(col, 'asc') }"
                    width="10"
                    height="16"
                    viewBox="0 0 10 16"
                  >
                    <path d="M5 2L9 6H1L5 2Z" fill="currentColor" />
                    <rect
                      x="4"
                      y="6"
                      width="2"
                      height="8"
                      fill="currentColor"
                    />
                  </svg>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in sortedData"
            :key="item.id || index"
            :class="{ selected: isSelected(item) }"
            @click="onRowClick(item, $event)"
            @mousedown="onRowMouseDown(item, $event)"
            @contextmenu.prevent="
              enableContextMenu
                ? onRowContextMenu(item, $event)
                : null
            "
          >
            <td v-if="selectable" class="select-cell">
              <BaseCheckbox
                :model-value="isSelected(item)"
                @update:model-value="toggleRow(item)"
              />
            </td>
            <td
              v-for="col in columns"
              :key="col.key"
              class="table-cell"
            >
              <slot
                :name="`cell-${col.key}`"
                :item="item"
                :value="item[col.key]"
              >
                <TableCellContent
                  :item="item"
                  :column="col"
                  @confirm-repair-inspection="
                    $emit('confirm-repair-inspection', $event)
                  "
                />
              </slot>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Контекстное меню строки -->
      <BaseContextMenu
        :visible="enableContextMenu && contextMenu.visible"
        :x="contextMenu.x"
        :y="contextMenu.y"
        :items="resolvedContextMenuItems"
        @close="closeContextMenu"
      />
    </div>

    <div
      v-show="hasHorizontalOverflow"
      class="table-scrollbar--sticky"
      role="scrollbar"
      aria-label="Горизонтальная прокрутка таблицы"
      aria-orientation="horizontal"
      aria-valuemin="0"
      :aria-valuemax="maxHorizontalScroll"
      :aria-valuenow="Math.round(horizontalScrollLeft)"
      tabindex="0"
      @pointerdown.prevent="scrollFromStickyTrack"
      @keydown="handleStickyScrollbarKeydown"
    >
      <div
        class="table-scrollbar--sticky__thumb"
        :style="{
          width: `${stickyScrollbarThumbWidth}px`,
          transform: `translateX(${stickyScrollbarThumbOffset}px)`,
        }"
        @pointerdown.stop.prevent="startStickyScrollbarDrag"
      />
    </div>
  </div>
</template>

<script>
import TableCellContent from "@/components/Table/TableCellContent.vue";
import { isAttachmentClickTarget } from "@/utils/tableRowClickUtils";

export default {
  name: "TableComponent",

  components: {
    TableCellContent,
  },

  props: {
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
    columns: {
      type: Array,
      required: true,
      default: () => [],
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    enableContextMenu: {
      type: Boolean,
      default: true,
    },
    // Дополнительные пункты контекстного меню от родителя
    extraContextMenuItems: {
      type: Array,
      default: () => [],
    },
    resetSelection: {
      type: Number,
      default: 0,
    },
    ready: {
      type: Boolean,
      default: false,
    },
    sortFields: {
      type: Array,
      default: () => [],
    },
    serverSort: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "row-click",
    "row-middle-click",
    "selection-change",
    "sort-change",
    "accept-expense",
    "confirm-repair-inspection",
    "vehicle-link-click",
    "driver-link-click",
    "counterparty-link-click",
    "context-menu-action",
  ],
  data() {
    return {
      selectedRows: [],
      localSort: {
        key: null,
        order: "asc",
      },
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        item: null,
      },
      hasHorizontalOverflow: false,
      horizontalScrollLeft: 0,
      maxHorizontalScroll: 0,
      stickyScrollbarThumbWidth: 0,
      stickyScrollbarThumbOffset: 0,
      stickyScrollbarDragStartX: null,
      stickyScrollbarDragStartScrollLeft: 0,
      stickyScrollbarPointerId: null,
      tableResizeObserver: null,
    };
  },
  watch: {
    resetSelection() {
      this.selectedRows = [];
      this.$emit("selection-change", []);
    },
    ready(isReady) {
      if (!isReady) {
        this.removeTableResizeObserver();
        return;
      }

      this.$nextTick(this.setupHorizontalScroll);
    },
  },
  computed: {
    isTableReady() {
      return this.ready;
    },

    allSelected() {
      return (
        this.data.length > 0 &&
        this.selectedRows.length === this.data.length
      );
    },

    sortedData() {
      if (!this.localSort.key) {
        return this.data;
      }

      return [...this.data].sort((a, b) => {
        const column = this.columns.find(
          (col) => col.key === this.localSort.key
        );
        const aValue = this.getSortValue(a, column);
        const bValue = this.getSortValue(b, column);

        // Обработка разных типов данных
        if (aValue === bValue) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        let result = 0;
        if (
          typeof aValue === "number" &&
          typeof bValue === "number"
        ) {
          result = aValue - bValue;
        } else if (
          typeof aValue === "string" &&
          typeof bValue === "string"
        ) {
          result = aValue.localeCompare(bValue, "ru");
        } else {
          result = aValue < bValue ? -1 : 1;
        }

        return this.localSort.order === "asc" ? result : -result;
      });
    },

    resolvedContextMenuItems() {
      const defaultItem = {
        key: "open-new-tab",
        label: "Открыть в новой вкладке",
        icon: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
          <polyline points="15 3 21 3 21 9"/>
          <line x1="10" y1="14" x2="21" y2="3"/>
        </svg>`,
        action: () => this.openInNewTab(),
      };

      const extraItems = this.extraContextMenuItems.map(
        (externalItem) => ({
          ...externalItem,
          action: () => {
            this.$emit("context-menu-action", {
              key: externalItem.key,
              item: this.contextMenu.item,
            });
            this.closeContextMenu();
          },
        })
      );

      return [defaultItem, ...extraItems];
    },
  },
  methods: {
    setupHorizontalScroll() {
      this.removeTableResizeObserver();
      this.updateHorizontalScrollbar();

      if (!window.ResizeObserver) return;

      this.tableResizeObserver = new window.ResizeObserver(
        this.updateHorizontalScrollbar
      );
      [this.$refs.tableWrapper, this.$refs.table]
        .filter(Boolean)
        .forEach((element) => this.tableResizeObserver.observe(element));
    },

    removeTableResizeObserver() {
      this.tableResizeObserver?.disconnect();
      this.tableResizeObserver = null;
    },

    updateHorizontalScrollbar() {
      const tableWrapper = this.$refs.tableWrapper;
      if (!tableWrapper) return;

      const { clientWidth, scrollWidth, scrollLeft } = tableWrapper;

      this.hasHorizontalOverflow =
        scrollWidth > clientWidth + 1;
      this.maxHorizontalScroll = Math.max(0, scrollWidth - clientWidth);
      this.horizontalScrollLeft = scrollLeft;

      const proportionalWidth = (clientWidth * clientWidth) / scrollWidth;
      this.stickyScrollbarThumbWidth = Math.min(
        clientWidth,
        Math.max(64, proportionalWidth)
      );

      const availableTrackWidth = Math.max(
        0,
        clientWidth - this.stickyScrollbarThumbWidth
      );
      this.stickyScrollbarThumbOffset = this.maxHorizontalScroll
        ? (scrollLeft / this.maxHorizontalScroll) * availableTrackWidth
        : 0;
    },

    syncScrollFromBottom() {
      this.updateHorizontalScrollbar();
    },

    setHorizontalScroll(scrollLeft) {
      const tableWrapper = this.$refs.tableWrapper;
      if (!tableWrapper) return;

      tableWrapper.scrollLeft = Math.min(
        this.maxHorizontalScroll,
        Math.max(0, scrollLeft)
      );
      this.updateHorizontalScrollbar();
    },

    scrollFromStickyTrack(event) {
      const trackRect = event.currentTarget.getBoundingClientRect();
      const availableTrackWidth = Math.max(
        1,
        trackRect.width - this.stickyScrollbarThumbWidth
      );
      const targetOffset =
        event.clientX -
        trackRect.left -
        this.stickyScrollbarThumbWidth / 2;
      const scrollRatio = Math.min(
        1,
        Math.max(0, targetOffset / availableTrackWidth)
      );

      this.setHorizontalScroll(scrollRatio * this.maxHorizontalScroll);
    },

    startStickyScrollbarDrag(event) {
      if (event.pointerType === "mouse" && event.button !== 0) return;

      this.stickyScrollbarDragStartX = event.clientX;
      this.stickyScrollbarDragStartScrollLeft = this.horizontalScrollLeft;
      this.stickyScrollbarPointerId = event.pointerId;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      document.addEventListener("pointermove", this.dragStickyScrollbar);
      document.addEventListener("pointerup", this.stopStickyScrollbarDrag);
      document.addEventListener(
        "pointercancel",
        this.stopStickyScrollbarDrag
      );
    },

    dragStickyScrollbar(event) {
      if (
        this.stickyScrollbarDragStartX === null ||
        event.pointerId !== this.stickyScrollbarPointerId
      )
        return;

      const tableWrapper = this.$refs.tableWrapper;
      if (!tableWrapper) return;

      const availableTrackWidth = Math.max(
        1,
        tableWrapper.clientWidth - this.stickyScrollbarThumbWidth
      );
      const draggedPixels = event.clientX - this.stickyScrollbarDragStartX;
      const scrollDelta =
        (draggedPixels / availableTrackWidth) * this.maxHorizontalScroll;

      this.setHorizontalScroll(
        this.stickyScrollbarDragStartScrollLeft + scrollDelta
      );
    },

    stopStickyScrollbarDrag(event) {
      if (
        event &&
        this.stickyScrollbarPointerId !== null &&
        event.pointerId !== this.stickyScrollbarPointerId
      )
        return;

      this.stickyScrollbarDragStartX = null;
      this.stickyScrollbarPointerId = null;
      document.removeEventListener("pointermove", this.dragStickyScrollbar);
      document.removeEventListener(
        "pointerup",
        this.stopStickyScrollbarDrag
      );
      document.removeEventListener(
        "pointercancel",
        this.stopStickyScrollbarDrag
      );
    },

    handleStickyScrollbarKeydown(event) {
      const keyboardScrollOffsets = {
        ArrowLeft: -48,
        ArrowRight: 48,
        PageUp: -this.$refs.tableWrapper?.clientWidth,
        PageDown: this.$refs.tableWrapper?.clientWidth,
        Home: -this.maxHorizontalScroll,
        End: this.maxHorizontalScroll,
      };

      const scrollOffset = keyboardScrollOffsets[event.key];
      if (scrollOffset === undefined) return;

      event.preventDefault();
      this.setHorizontalScroll(this.horizontalScrollLeft + scrollOffset);
    },

    isServerSortable(columnKey) {
      return this.sortFields.includes(columnKey);
    },

    getSortValue(item, column) {
      const numericKeys = [
        "price",
        "amount",
        "total",
        "id",
        "odometer",
        "quantity",
        "cargo_weight",
        "cargo_volume",
        "min_temperature",
        "max_temperature",
      ];

      const dateKeys = [
        "created_at",
        "updated_at",
        "order_date",
        "date",
        "starts_at",
        "ends_at",
        "start_date",
        "end_date",
        "payment_date",
        "spent_at",
        "actual_started_at",
        "actual_finished_at",
        "planned_started_at",
        "planned_finished_at",
        "actual_pickup_at",
        "actual_delivery_at",
        "planned_pickup_at",
        "planned_delivery_at",
        "confirmed_at",
      ];

      const rawValue = column.key.includes(".")
        ? column.key
            .split(".")
            .reduce(
              (acc, key) => (acc != null ? acc[key] : null),
              item
            )
        : item[column.key];

      if (numericKeys.includes(column.key)) {
        if (
          rawValue === null ||
          rawValue === undefined ||
          rawValue === ""
        )
          return null;
        return parseFloat(rawValue) || 0;
      }

      if (dateKeys.includes(column.key)) {
        if (!rawValue) return null;
        return new Date(rawValue).getTime();
      }

      if (column.formatter) {
        const formatted = column.formatter(rawValue, item);
        if (this.isComponentFormatterResult(formatted)) {
          return formatted.text || "";
        }

        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = formatted;
        return tempDiv.textContent || tempDiv.innerText || "";
      }

      return rawValue;
    },

    isComponentFormatterResult(value) {
      return Boolean(
        value && typeof value === "object" && value.component
      );
    },

    isSelected(item) {
      return this.selectedRows.includes(item);
    },

    toggleRow(item) {
      const index = this.selectedRows.indexOf(item);

      if (index > -1) {
        this.selectedRows.splice(index, 1);
      } else {
        if (this.multiple) {
          this.selectedRows.push(item);
        } else {
          this.selectedRows = [item];
        }
      }

      this.$emit("selection-change", this.selectedRows);
    },

    toggleAllRows(value) {
      if (value) {
        this.selectedRows = [...this.data];
      } else {
        this.selectedRows = [];
      }

      this.$emit("selection-change", this.selectedRows);
    },

    onRowClick(item, event) {
      const clickedCell = event.target.closest("td");
      if (
        clickedCell &&
        clickedCell.classList.contains("select-cell")
      ) {
        return;
      }

      // Клик по кликабельной сущности (транспорт, водитель, контрагент)
      const entityLink = event.target.closest(".entity-link");
      if (entityLink) {
        const entityType = entityLink.dataset.entityType;
        const entityId = entityLink.dataset.entityId;
        if (!entityType && entityLink.tagName === "A") return;

        if (entityType === "vehicle") {
          this.$emit("vehicle-link-click", entityId);
        } else if (entityType === "driver") {
          this.$emit("driver-link-click", entityId);
        } else if (entityType === "counterparty") {
          this.$emit("counterparty-link-click", entityId);
        }
        return;
      }

      // Клик по кнопке подтверждения расхода
      const acceptBtn = event.target.closest(".expense-accept-btn");
      if (acceptBtn) {
        const expenseId = acceptBtn.dataset.expenseId;
        this.$emit("accept-expense", parseInt(expenseId));
        return;
      }

      if (isAttachmentClickTarget(event.target)) {
        return;
      }

      if (
        event.target.tagName === "INPUT" ||
        event.target.closest(".base-checkbox")
      ) {
        return;
      }

      const selection = window.getSelection();
      if (selection.toString().length > 0) {
        return;
      }

      // Ctrl/Cmd + click — открыть в новой вкладке + обычная навигация
      if (event.ctrlKey || event.metaKey) {
        this.$emit("row-middle-click", item);
        return;
      }

      this.$emit("row-click", item);
    },

    onRowMouseDown(item, event) {
      // Средняя кнопка мыши
      if (event.button === 1) {
        event.preventDefault();
        this.$emit("row-middle-click", item);
      }
    },

    // TODO: вынести общую механику context menu в helper/composable.
    onRowContextMenu(item, event) {
      const menuWidth = 220;
      const menuHeight = 44 * (1 + this.extraContextMenuItems.length);

      let x = event.clientX;
      let y = event.clientY;

      if (x + menuWidth > window.innerWidth)
        x = window.innerWidth - menuWidth - 8;
      if (y + menuHeight > window.innerHeight)
        y = window.innerHeight - menuHeight - 8;

      this.contextMenu = {
        visible: true,
        x,
        y,
        item,
      };
    },

    openInNewTab() {
      if (this.contextMenu.item) {
        this.$emit("row-middle-click", this.contextMenu.item);
      }
      this.closeContextMenu();
    },

    closeContextMenu() {
      this.contextMenu.visible = false;
      this.contextMenu.item = null;
    },

    onKeyDown(event) {
      if (event.key === "Escape") {
        this.closeContextMenu();
      }
    },

    handleSort(column) {
      if (this.isServerSortable(column.key)) {
        this.localSort.key = null;

        const direction =
          this.serverSort?.field === column.key &&
          this.serverSort?.direction === "asc"
            ? "desc"
            : "asc";

        this.$emit("sort-change", {
          field: column.key,
          direction,
        });
      } else {
        if (this.localSort.key === column.key) {
          this.localSort.order =
            this.localSort.order === "asc" ? "desc" : "asc";
        } else {
          this.localSort.key = column.key;
          this.localSort.order = "asc";
        }
      }
    },

    isSortActive(column, order) {
      if (this.isServerSortable(column.key)) {
        return (
          this.serverSort?.field === column.key &&
          this.serverSort?.direction === order
        );
      }
      return (
        this.localSort.key === column.key &&
        this.localSort.order === order
      );
    },
  },
  mounted() {
    document.addEventListener("click", this.closeContextMenu);
    document.addEventListener("keydown", this.onKeyDown);
    this.$nextTick(this.setupHorizontalScroll);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeContextMenu);
    document.removeEventListener("keydown", this.onKeyDown);
    this.stopStickyScrollbarDrag();
    this.removeTableResizeObserver();
  },
};
</script>

<style scoped>
.table-container {
  position: relative;
  width: 100%;
}

.table-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(1px);
  pointer-events: none;
}

.table-overlay__spinner {
  position: sticky;
  top: 0;
  height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-15%);
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background: #fff;
  -webkit-overflow-scrolling: touch;
}

.table-wrapper::-webkit-scrollbar {
  display: none;
}

.table-scrollbar--sticky {
  position: sticky;
  bottom: var(--app-content-sticky-bottom-offset, 0);
  z-index: 4;
  width: 100%;
  height: 24px;
  margin-top: 0;
  padding: 7px 0;
  box-sizing: border-box;
  border-radius: 0;
  cursor: pointer;
  user-select: none;
  touch-action: none;
}

.table-scrollbar--sticky::before {
  content: "";
  position: absolute;
  inset-block: 0;
  inset-inline: var(--app-content-sticky-inline-offset, 0);
  z-index: 0;
  border-top: 1px solid rgba(15, 118, 110, 0.12);
  background: #f8faf9;
  box-shadow: 0 -2px 6px rgba(15, 23, 42, 0.06);
  pointer-events: none;
}

.table-scrollbar--sticky__thumb {
  position: relative;
  z-index: 1;
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(
    90deg,
    var(--color-green-hover),
    var(--color-green)
  );
  box-shadow: 0 1px 4px rgba(0, 152, 130, 0.3);
  cursor: grab;
  transition: filter 0.15s ease, box-shadow 0.15s ease;
  will-change: transform;
}

.table-scrollbar--sticky__thumb:hover {
  filter: brightness(0.94);
  box-shadow: 0 2px 6px rgba(0, 152, 130, 0.4);
}

.table-scrollbar--sticky__thumb:active {
  cursor: grabbing;
}

.table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

.table-header,
.table-cell {
  padding: 16px 20px;
  max-width: 400px;
  text-align: left;
  vertical-align: middle;
  word-wrap: break-word;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table-cell > span {
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table-header {
  position: sticky;
  top: 0;
  background-color: var(--color-dark);
  color: #fff;
  font-weight: 600;
  white-space: nowrap;
  user-select: none;
}

.table-header.sortable {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.table-header.sortable:hover {
  background-color: #2d2d47;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.header-text {
  flex: 1;
}

.sort-indicator {
  margin-left: 8px;
}

.sort-arrow {
  color: rgba(255, 255, 255, 0.4);
  transition: all 0.2s ease;
  opacity: 0.6;
}

.sort-arrow.active {
  color: #fff;
  opacity: 1;
  transform: scale(1.1);
}

.table-header.sortable:hover .sort-arrow {
  opacity: 0.8;
}

.table-cell {
  cursor: pointer;
}

.table tr {
  cursor: pointer;
}

.table tr:nth-child(even) {
  background-color: #e4e4e4;
}

.table tr:hover {
  background-color: #bde4df;
}

.select-column,
.select-cell {
  text-align: center;
  width: 60px;
  min-width: 60px;
  white-space: nowrap;
  box-sizing: border-box;
}

.select-column {
  background-color: var(--color-dark);
}

tr.selected {
  background-color: #e6f7ff !important;
}
</style>
